import { useCallback, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import type { PageState, LocationLink, ServerMessage, SerializedToken } from '@roll1/shared';
import type { Token, Stroke, FogReveal } from '../types';
import { useSessionContext } from '../contexts/SessionContext';
import { useCanvasContext } from '../contexts/CanvasContext';
import { useTokenContext } from '../contexts/TokenContext';
import { useChatContext } from '../contexts/ChatContext';
import { useInitiativeContext } from '../contexts/InitiativeContext';
import { useI18n } from '../i18n';
import type { TranslationKeys } from '../i18n/types';
import { useAlertContext } from '../contexts/AlertContext';
import { useUndoContext } from '../contexts/UndoContext';
import { serializeToken } from '../lib/token-utils';
import { showInitiativeNotices } from '../lib/initiative-view';
import { INITIATIVE_OPEN_EVENT } from '../lib/initiative-requests';
import { stepPart } from '../components/chronicle/time/worldTime';
import { usePageHydration } from './usePageHydration';
import { applyEchoToCanvas, applyEchoToStoredPage, applySharedPageEcho, canvasEchoPageId, isOnViewedMap } from '../lib/canvas-page-echo';

interface SyncOptions {
  onJournalRevealed?: () => void;
  onInitiativePanelToggle?: (visible: boolean) => void;
  onPing?: (x: number, y: number, by: string, color?: string) => void;
}

export function useMultiplayerSync({ onJournalRevealed, onInitiativePanelToggle, onPing }: SyncOptions) {
  const { mp, pages, isGM, outgoingPageId, setSessionSettings } = useSessionContext();
  const { engine, drawing, fog, dynamicFog, dynamicFogTokensRef, globalIlluminationRef, wallsHook, locationLinks, mapObjects } = useCanvasContext();
  const { tokens, notes: notesHook, pins: pinsHook } = useTokenContext();
  const { chat, rolls, sounds } = useChatContext();
  const { initiative } = useInitiativeContext();
  const { t } = useI18n();
  const alertCtx = useAlertContext();
  const undoStack = useUndoContext();

  // Fog is a projection of the live token store, including aura, transfer and
  // snapshot updates. Maintaining it in individual message cases misses paths.
  useEffect(() => {
    dynamicFogTokensRef.current = tokens.tokens.map(serializeToken);
    dynamicFog.invalidateVisibility();
    dynamicFog.invalidateLighting();
  }, [tokens.tokens, dynamicFogTokensRef, dynamicFog.invalidateVisibility, dynamicFog.invalidateLighting]);

  // Page save/load/switch mechanism, extracted to usePageHydration so GM Studio
  // can reuse it (2026-07-29). Behavior unchanged: useMultiplayerSync still owns
  // the WS message router below and consumes these in its handlers.
  const { saveCurrentPageToStore, loadPageToHooks, fogSentEnabled } = usePageHydration();
  const lastCommandErrorRef = useRef<{ key: string; ts: number } | null>(null);
  // The journal store is no longer a dep of the router below (it moved to
  // useSessionStoreSync), so read the UI callback through a ref, not a stale closure.
  const onJournalRevealedRef = useRef(onJournalRevealed);
  onJournalRevealedRef.current = onJournalRevealed;
  const aiTypingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Store hydration from the join snapshot lives in useSessionStoreSync (above
  // the StudioGate fork, once per join). Only the table's own UI is restored
  // here: the GM-opened initiative panel — the live initiative:panel-updated
  // broadcast misses late joiners and reloads, so an active combat's panel
  // silently vanished for players. Runs on mount and on every new join.
  useEffect(() => {
    const s = mp.serverState;
    if (!s?.initiative) return;
    // Initiative brief 25.10 (Р11): the GM's window comes back as he left it; a player's opens by itself while a fight
    // is on (and never before one — the GM opening an empty window no longer pops it up at the players).
    const fightOn = (s.initiative.entries?.length ?? 0) > 0;
    if (mp.participant?.role === 'gm' ? s.initiative.panelVisible === true : fightOn) {
      onInitiativePanelToggle?.(true);
    }
  }, [mp.joinSeq]); // eslint-disable-line react-hooks/exhaustive-deps

  // WS disconnect toast
  const prevStatusRef = useRef(mp.status);
  useEffect(() => {
    const prev = prevStatusRef.current;
    prevStatusRef.current = mp.status;
    if (mp.session && prev === 'connected' && mp.status === 'disconnected') {
      toast.error(t('session.connectionLost'));
    }
    if (mp.session && prev === 'disconnected' && mp.status === 'connected') {
      toast.success(t('session.connectionRestored'));
      // Reload current page to ensure canvas state is fresh after reconnect
      const pid = pages.viewingPageId;
      if (pid) {
        const page = pages.getPageData(pid);
        if (page) loadPageToHooks(page);
      }
    }
  }, [mp.status, mp.session]); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for multiplayer messages to sync the canvas + table UI. Material
  // stores (journal, sheets, templates, page list, batch reconciles) are routed
  // by useSessionStoreSync; only their table UI side-effects remain below.
  useEffect(() => {
    return mp.onMessage((msg: ServerMessage) => {
      // Page content: the canvas copy of the viewed page, else its store copy —
      // one implementation for the table and GM Studio (lib/canvas-page-echo).
      applySharedPageEcho(pages, msg);
      const echoPageId = canvasEchoPageId(msg);
      if (echoPageId !== null) {
        if (echoPageId === pages.viewingPageId) {
          applyEchoToCanvas(
            { tokens, notes: notesHook, pins: pinsHook, drawing, fog, dynamicFog, engine, wallsHook, mapObjects, locationLinks, globalIlluminationRef },
            msg,
            pages.getPageData(echoPageId),
          );
        } else {
          const pd = pages.getPageData(echoPageId);
          if (pd) applyEchoToStoredPage(pd, msg);
        }
      }

      switch (msg.type) {
        case 'chat:received':
          chat.addMessage(msg.message);
          if (msg.message.type === 'roll') {
            sounds.playDiceSound();
          } else if (msg.message.type !== 'system') {
            sounds.playMessageSound();
          }
          break;
        case 'chat:image-update':
          chat.updateMessage(msg.messageId, {
            imageStatus: msg.imageStatus,
            ...(msg.imageUrl ? { imageUrl: msg.imageUrl } : {}),
            // Deleted in the gallery (group Q): the message keeps its prompt, the picture is gone.
            ...(msg.imageStatus === 'deleted' ? { imageUrl: undefined } : {}),
          });
          break;
        case 'chat:ai-typing': {
          const phase = msg.typing
            ? (msg.phase as 'connecting' | 'thinking' | 'generating') || 'connecting'
            : false;
          chat.setAiTyping(phase);
          // Clear any existing timeout
          if (aiTypingTimeoutRef.current) {
            clearTimeout(aiTypingTimeoutRef.current);
            aiTypingTimeoutRef.current = null;
          }
          // Auto-hide after 30s fallback if typing
          if (phase) {
            aiTypingTimeoutRef.current = setTimeout(() => {
              chat.setAiTyping(false);
              aiTypingTimeoutRef.current = null;
            }, 30_000);
          }
          break;
        }
        // GJ-17: a long rest takes the night, but the world's clock is the GM's: it is OFFERED, not moved by the
        // player's button. One offer for a party that rests together (same toast id); a click moves it by 3 parts of day.
        case 'sheet:rest-completed': {
          const cursor = mp.serverState?.timelineCursor;
          if (isGM && msg.restType === 'long' && cursor !== undefined && cursor.day != null) {
            const calendar = mp.serverState?.calendar;
            toast(t('rest.shiftOffer'), {
              id: 'long-rest-time',
              duration: 20_000,
              action: {
                label: t('rest.shiftAction'),
                onClick: (): void => {
                  const latest = mp.serverState?.timelineCursor ?? cursor;
                  let next = latest;
                  for (let i = 0; i < 3; i++) next = stepPart(next, 1, calendar, t('timeline.cursor.dayFormat'));
                  mp.send({ type: 'timeline:update-cursor', cursor: next });
                },
              },
            });
          }
          break;
        }
        case 'initiative:updated':
          initiative.applyState(msg);
          if (msg.notices && msg.notices.length) showInitiativeNotices(msg.notices, msg.undo, (undoId) => mp.send({ type: 'initiative:undo', undoId } as never), t as never);
          break;
        case 'initiative:panel-updated':
          onInitiativePanelToggle?.(msg.visible);
          // A fight started: the phone brings its «Иниц.» tab forward too (Р11/Р12).
          if (msg.visible && mp.participant?.role !== 'gm') window.dispatchEvent(new CustomEvent(INITIATIVE_OPEN_EVENT));
          break;
        case 'page:created': toast.success(`${msg.page.name} ${t('session.pageCreated')}`); break;
        case 'page:snapshot-created': toast.success(`${msg.page.name} ${t('session.pageCreated')}`); break;
        // page:activated is routed by useSessionStoreSync (both surfaces).
        case 'page:tokens-transferred':
          saveCurrentPageToStore();
          pages.handleTokensTransferred(msg.sourcePageId, msg.targetPageId, msg.tokenIds, msg.tokens);
          // A move that is part of a map show is reported by the show's own message (useShowMapToPlayers).
          if (msg.withShow !== true) toast.success(t('mapView.carried').replace('{n}', String(msg.tokenIds.length)));
          if (msg.sourcePageId === pages.viewingPageId || msg.targetPageId === pages.viewingPageId) {
            const page = pages.getPageData(pages.viewingPageId);
            if (page) { tokens.setTokens(page.tokens.map((t) => ({ ...t, imageEl: null }))); engine.markAllDirty(); }
          }
          break;
        case 'sheet:created': toast.success(`${msg.sheet.name} ${t('session.sheetCreated')}`); break;
        case 'roll:result': rolls.handleRollResult(msg.rollResult); chat.addSheetRollMessage(msg.rollResult); sounds.playDiceSound(); break;
        // A ping / ruler belongs to a map: shown only on the map this table looks at (no pageId — the players' map).
        case 'ruler:shown': if (isOnViewedMap(msg.pageId, pages.viewingPageId, pages.activePageId)) drawing.showPublicRuler({ by: msg.by, points: msg.points, totalDistance: msg.totalDistance, hideAt: msg.persistent ? 0 : Date.now() + 5000 }); break;
        case 'ruler:hidden': if (isOnViewedMap(msg.pageId, pages.viewingPageId, pages.activePageId)) drawing.hidePublicRuler(msg.by); break;
        case 'ping:showed': if (isOnViewedMap(msg.pageId, pages.viewingPageId, pages.activePageId)) onPing?.(msg.x, msg.y, msg.by, msg.color); break;
        case 'settings:updated': setSessionSettings(msg.settings); break;
        case 'location:navigation-mode-changed': pages.handleNavigationModeChanged(msg.mode); break;
        case 'location:navigated': break;
        case 'location:navigate-request': break;
        case 'alert:created':
          alertCtx.setAlerts(prev => [...prev, msg.alert]);
          break;
        case 'alert:updated':
          alertCtx.setAlerts(prev => prev.map(a => a.id === msg.alertId ? { ...a, ...msg.changes } : a));
          break;
        case 'alert:deleted':
          alertCtx.setAlerts(prev => prev.filter(a => a.id !== msg.alertId));
          break;
        case 'alert:fired':
          alertCtx.queue.enqueue(msg.alert);
          break;
        case 'alert:reordered':
          alertCtx.setAlerts(prev => {
            const newAlerts = [...prev];
            for (let i = 0; i < msg.alertIds.length; i++) {
              const a = newAlerts.find(x => x.id === msg.alertIds[i]);
              if (a) a.sortOrder = i;
            }
            return newAlerts;
          });
          break;
        case 'session:error': chat.addSystemMessage(msg.message); toast.error(msg.message); break;
        // A sheet dropped on a map: the character's own token came instead of the drawn one — its «create» leaves the undo
        // stack, or Ctrl+Z / Ctrl+Y would remove nothing and then make an unbound double (31.10).
        case 'token:deleted': if (msg.placedInstead !== undefined) undoStack.dropByEntityId(msg.tokenId); break;
        case 'command:error': {
          // Roll back an optimistic ghost the server rejected. token:create adds
          // the token locally before the server acks; on rejection (permission
          // denied, invalid page) the ghost otherwise lingered on the actor's
          // canvas forever. entityId is the exact token to drop.
          if (msg.command === 'token:create' && msg.entityId) {
            const ghostId = msg.entityId;
            tokens.removeToken(ghostId);
            // Also drop the matching undo command — otherwise Ctrl+Z would
            // "undo" the already-removed ghost (wasting a slot) and Redo would
            // re-summon it into another rejection.
            undoStack.dropByEntityId(ghostId);
            // Sweep page stores: if the player switched pages during the
            // create→reject round-trip, the ghost was snapshotted into the
            // origin page's store, which removeToken (live page only) misses —
            // it would reload the moment the player returns to that page.
            for (const p of pages.pages) {
              const pd = pages.getPageData(p.id);
              if (pd && pd.tokens.some((tk) => tk.id === ghostId)) {
                pages.setPageData(p.id, { ...pd, tokens: pd.tokens.filter((tk) => tk.id !== ghostId) });
              }
            }
          }
          // An undo of an AI answer is reported by the AI chat itself (its own words, its own state).
          if (msg.command === 'ai:undo') break;
          const key = `${msg.command}:${msg.code ?? ''}:${msg.error}`;
          const now = Date.now();
          const last = lastCommandErrorRef.current;
          if (!last || last.key !== key || now - last.ts > 1500) {
            const errorKey = msg.code ? `error.${msg.code}` : '';
            const translated = errorKey ? t(errorKey as keyof TranslationKeys) : msg.error;
            const errorText = (translated && translated !== errorKey) ? translated : msg.error;
            toast.error(errorText);
            lastCommandErrorRef.current = { key, ts: now };
          }
          break;
        }
        case 'session:created':
          toast.success(t('session.sessionCreated'));
          break;
        case 'session:joined':
          toast.success(t('session.sessionJoined'));
          break;
        case 'journal:revealed': onJournalRevealedRef.current?.(); break;
        case 'participant:joined':
          if (msg.participant.id !== mp.participant?.id) toast.info(`${msg.participant.name} ${t('session.participantJoined')}`);
          break;
        case 'participant:left': {
          const lp = mp.participants.find((p) => p.id === msg.participantId);
          if (lp && lp.id !== mp.participant?.id) toast.info(`${lp.name} ${t('session.participantLeft')}`);
          break;
        }
      }
    });
  }, [mp, tokens, chat, drawing, fog, initiative, engine, pages, rolls, sounds, saveCurrentPageToStore, locationLinks, mapObjects, notesHook, pinsHook, setSessionSettings, alertCtx, onInitiativePanelToggle, onPing, wallsHook]); // eslint-disable-line

  return {
    saveCurrentPageToStore,
    loadPageToHooks,
    serializeToken,
    fogSentEnabled,
  };
}
